import React, { useEffect, useState, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import {
  Save,
  Plus,
  X,
  Phone,
  Mail,
  MapPin,
  Search,
  Receipt,
  CalendarClock,
  Building2,
  User,
  CreditCard,
  ShieldAlert,
  Lock,
  Trash2,
  Wallet,
} from "lucide-react";
import { IconTextButton } from "../../../components/ButtonComponents";
import { ETabs } from "../../../components/ETabs";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import {
  Customer,
  CustomerFormState,
  AccountAddress,
} from "../../../types/customer";
import { useEmployeeStore } from "../../../store/employeeStore";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { Employee } from "../../../types/employee";
import api from "../../../service/api";

interface CustomerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
  onSave: (data: any) => void;
}

const initialData: CustomerFormState = {
  name: "",
  phone: "",
  email: "",
  taxNumber: "",
  taxOffice: "",
  defaultPriceListCode: 1,
  paymentPlanCode: "",
  riskLimit: 0,
  riskLimitType: "UNLIMITED",
  defaultDueDays: 0,
  isBlocked: false,
  blockReason: "",
  temporaryLimit: 0,
  isEInvoiceUser: false,
  eInvoiceAlias: "",
  representativeId: null,
  temporaryLimitEndAt: null,
  fixedDiscountRate: 0,
  turnoverPremium: 0,
  poolBalance: 0,
  isDealer: false,
  includeInPoolEarn: true,
  canUsePoolDiscount: true,
  collectionPoolType: "PERSONAL",
  customEarnRate: null,
  customMaxDiscountRate: null,
  addresses: [],
};

export const CustomerDialog: React.FC<CustomerDialogProps> = ({
  isOpen,
  onClose,
  customer,
  onSave,
}) => {
  const [formData, setFormData] = useState<CustomerFormState>(initialData);
  const {
    fetchDataDynamic: fetchEmployees,
    loading: empLoading,
    data: employees,
  } = useEmployeeStore();
  const employeeSelect = useSelectDialog<Employee>();
  const [activeIndex, setActiveIndex] = useState(0);
  const [displayNames, setDisplayNames] = useState({ employeeName: "" });
  const [priceListOptions, setPriceListOptions] = useState<any[]>([]);

  useEffect(() => {
    api.get("/price/lists/firm-lists")
      .then((res) => {
        if (res.data) {
          setPriceListOptions(res.data);
        }
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (customer) {
      setFormData({
        ...customer,
        name: customer.name || "",
        phone: customer.phone || "",
        email: customer.email || "",
        taxNumber: customer.taxNumber || "",
        taxOffice: customer.taxOffice || "",
        defaultPriceListCode: customer.defaultPriceListCode || 1,
        paymentPlanCode: customer.paymentPlanCode || "",
        riskLimit: customer.riskLimit || 0,
        riskLimitType: customer.riskLimitType || "UNLIMITED",
        defaultDueDays: customer.defaultDueDays || 0,
        isBlocked: customer.isBlocked || false,
        blockReason: customer.blockReason || "",
        temporaryLimit: customer.temporaryLimit || 0,
        fixedDiscountRate: customer.fixedDiscountRate || 0,
        turnoverPremium: customer.turnoverPremium || 0,
        poolBalance: customer.poolBalance || 0,
        isDealer: customer.isDealer || false,
        includeInPoolEarn: customer.includeInPoolEarn ?? true,
        canUsePoolDiscount: customer.canUsePoolDiscount ?? true,
        collectionPoolType: customer.collectionPoolType || "PERSONAL",
        customEarnRate: customer.customEarnRate || null,
        customMaxDiscountRate: customer.customMaxDiscountRate || null,
        addresses: customer.addresses?.map((a) => ({ ...a })) || [],
      });
    } else {
      setFormData(initialData);
    }
    if (customer && customer.representative) {
      setDisplayNames({
        employeeName: `${customer.representative.firstName} ${customer.representative.lastName}`,
      });
    } else {
      setDisplayNames({ employeeName: "" });
    }
    setActiveIndex(0);
  }, [isOpen, customer]);

  const openEmployeeSelect = async () => {
    const res = await fetchEmployees({ pageIndex: 1, pageSize: 10 });
    const selected = await employeeSelect.open({
      title: "Personel Seç",
      data: res?.items || [],
      // ARAMA ALANLARINI BURADA DÜZELTİYORUZ
      searchFields: [
        { field: "firstName", operator: "contains" },
        { field: "lastName", operator: "contains" }
      ],
      columns: [
        { field: "firstName", header: "Ad" },
        { field: "lastName", header: "Soyad" }
      ],
      onFilter: async (p) => {
        // p.filter artık iç içe geçmiş firstName ve lastName filtrelerini içerir
        const r = await fetchEmployees({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter });
        return { data: r.items, total: r.total };
      }
    });
    if (selected) {
      setFormData((p) => ({ ...p, representativeId: selected.id }));
      setDisplayNames((p) => ({
        ...p,
        employeeName: `${selected.firstName} ${selected.lastName}`,
      }));
    }
  };
  // ADRES YÖNETİMİ
  const addAddress = () => {
    const newAddr: AccountAddress = {
      id: `new-${Date.now()}`,
      title: "",
      addressType: "SHIPPING",
      isDefault: formData.addresses.length === 0,
      addressLine: "",
    };
    setFormData((p) => ({ ...p, addresses: [...p.addresses, newAddr] }));
  };

  const removeAddress = (index: number) => {
    setFormData((p) => ({
      ...p,
      addresses: p.addresses.filter((_, i) => i !== index),
    }));
  };

  const updateAddress = (
    index: number,
    field: keyof AccountAddress,
    value: any,
  ) => {
    const newAddrs = [...formData.addresses];
    if (field === "isDefault" && value === true) {
      newAddrs.forEach((a) => (a.isDefault = false));
    }
    newAddrs[index] = { ...newAddrs[index], [field]: value };
    setFormData((p) => ({ ...p, addresses: newAddrs }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Backend'in beklediği temiz payload'u oluşturuyoruz
    const payload = {
      name: formData.name,
      phone: formData.phone || null,
      email: formData.email || null,
      taxNumber: formData.taxNumber || null,
      taxOffice: formData.taxOffice || null,

      // BU ALANLAR HATA VERDİĞİ İÇİN ÇIKARTILDI:
      //isEInvoiceUser: formData.isEInvoiceUser,
      //  eInvoiceAlias: formData.eInvoiceAlias,
      representativeId: formData.representativeId ? Number(formData.representativeId) : null,
      defaultPriceListCode: formData.defaultPriceListCode,
      paymentPlanCode: formData.paymentPlanCode || null,

      // Risk ve Limit Alanları
      riskLimit: Number(formData.riskLimit || 0),
      riskLimitType: formData.riskLimitType,
      defaultDueDays: Number(formData.defaultDueDays || 0),
      isBlocked: !!formData.isBlocked,
      blockReason: formData.isBlocked ? formData.blockReason : null,
      temporaryLimit: Number(formData.temporaryLimit || 0),

      // İndirim ve Havuz Alanları
      fixedDiscountRate: Number(formData.fixedDiscountRate || 0),
      turnoverPremium: Number(formData.turnoverPremium || 0),
      poolBalance: Number(formData.poolBalance || 0),
      isDealer: !!formData.isDealer,
      includeInPoolEarn: !!formData.includeInPoolEarn,
      canUsePoolDiscount: !!formData.canUsePoolDiscount,
      collectionPoolType: formData.collectionPoolType,
      customEarnRate: formData.customEarnRate !== null ? Number(formData.customEarnRate) : null,
      customMaxDiscountRate: formData.customMaxDiscountRate !== null ? Number(formData.customMaxDiscountRate) : null,

      // Adresler
      addresses: formData.addresses.map((addr) => ({
        ...(typeof addr.id === "number" ? { id: addr.id } : {}),
        title: addr.title,
        addressType: addr.addressType,
        isDefault: !!addr.isDefault,
        addressLine: addr.addressLine || "",
        city: addr.city || null,
        district: addr.district || null,
        phone: addr.phone || null,
        contactPerson: addr.contactPerson || null,
      })),
    };

    onSave(payload);
    onClose();
  };

  const tabs = useMemo(
    () => [
      {
        id: "basic-info",
        header: (
          <>
            <User className="w-4 h-4 mr-2" /> Temel Bilgiler
          </>
        ),
        content: (
          <div className="space-y-4 p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">
                  Müşteri/Şirket Adı *
                </label>
                <InputText
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Vergi No / TCKN</label>
                <InputText
                  value={formData.taxNumber ?? ""}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, taxNumber: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Vergi Dairesi</label>
                <InputText
                  value={formData.taxOffice ?? ""}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, taxOffice: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">E-posta</label>
                <InputText
                  value={formData.email ?? ""}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, email: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Telefon</label>
                <InputText
                  value={formData.phone ?? ""}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, phone: e.target.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <CustomSelectInput
                  label="Sorumlu Plasiyer"
                  value={displayNames.employeeName || ""}
                  placeholder="Plasiyer Seçiniz..."
                  icon={User}
                  onClick={openEmployeeSelect}
                  onClear={formData.representativeId ? () => {
                    setFormData(p => ({ ...p, representativeId: null }));
                    setDisplayNames({ employeeName: "" });
                  } : undefined}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-3 bg-bgAlt rounded-lg border border-borderColor">
                <div className="flex items-center justify-between col-span-2 md:col-span-1">
                  <label className="font-bold text-sm text-primary flex items-center gap-2">
                    <Receipt size={16} /> e-Fatura Mükellefi
                  </label>
                  <InputSwitch
                    checked={formData.isEInvoiceUser}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, isEInvoiceUser: e.value }))
                    }
                  />
                </div>

                {formData.isEInvoiceUser && (
                  <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
                    <label className="font-bold text-[10px] text-primary uppercase">
                      e-Fatura Etiketi (Alias)
                    </label>
                    <InputText
                      value={formData.eInvoiceAlias ?? ""}
                      placeholder="urn:mail:defaultpk@..."
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          eInvoiceAlias: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "commercial",
        header: (
          <>
            <CreditCard className="w-4 h-4 mr-2" /> Ticari Bilgiler
          </>
        ),
        content: (
          <div className="space-y-4 p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">
                  Varsayılan Fiyat Listesi
                </label>
                <Dropdown
                  value={formData.defaultPriceListCode}
                  options={priceListOptions}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      defaultPriceListCode: e.value,
                    }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Ödeme Planı Kodu</label>
                <InputText
                  value={formData.paymentPlanCode ?? ""}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      paymentPlanCode: e.target.value,
                    }))
                  }
                  placeholder="Örn: 30GUN, NAKIT"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">
                  Varsayılan Vade (Gün)
                </label>
                <InputNumber
                  value={formData.defaultDueDays}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, defaultDueDays: e.value || 0 }))
                  }
                  suffix=" Gün"
                  min={0}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Sabit İskonto Oranı (%)</label>
                <InputNumber
                  value={formData.fixedDiscountRate}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, fixedDiscountRate: e.value || 0 }))
                  }
                  suffix=" %"
                  min={0}
                  max={100}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Ciro Primi (%)</label>
                <InputNumber
                  value={formData.turnoverPremium}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, turnoverPremium: e.value || 0 }))
                  }
                  suffix=" %"
                  min={0}
                  max={100}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Havuz Bakiyesi (TL)</label>
                <InputNumber
                  value={formData.poolBalance}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, poolBalance: e.value || 0 }))
                  }
                  mode="currency"
                  currency="TRY"
                  locale="tr-TR"
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "risk-limit",
        header: (
          <>
            <ShieldAlert className="w-4 h-4 mr-2" /> Risk & Limit
          </>
        ),
        content: (
          <div className="space-y-4 p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Risk Limit Tipi</label>
                <Dropdown
                  value={formData.riskLimitType}
                  options={[
                    { label: "Sınırsız", value: "UNLIMITED" },
                    { label: "Toplam Risk Limiti", value: "TOTAL_LIMIT" },
                    { label: "Aylık Limit", value: "MONTHLY_LIMIT" },
                  ]}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, riskLimitType: e.value }))
                  }
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Risk Limiti (TL)</label>
                <InputNumber
                  value={formData.riskLimit}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, riskLimit: e.value || 0 }))
                  }
                  mode="currency"
                  currency="TRY"
                  locale="tr-TR"
                  disabled={formData.riskLimitType === "UNLIMITED"}
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-bold text-sm">Geçici Limit (Ek)</label>
                <InputNumber
                  value={formData.temporaryLimit}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, temporaryLimit: e.value || 0 }))
                  }
                  mode="currency"
                  currency="TRY"
                  locale="tr-TR"
                />
              </div>
              <div className="flex flex-col gap-2 border p-3 rounded-lg bg-bgAlt">
                <div className="flex items-center justify-between">
                  <label className="font-bold text-sm text-danger flex items-center gap-2">
                    <Lock size={16} /> Cariyi Bloke Et
                  </label>
                  <InputSwitch
                    checked={formData.isBlocked}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, isBlocked: e.value }))
                    }
                  />
                </div>
                {formData.isBlocked && (
                  <div className="mt-2">
                    <label className="text-xs font-bold text-danger">
                      Bloke Nedeni
                    </label>
                    <InputTextarea
                      className="w-full mt-1"
                      value={formData.blockReason ?? ""}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          blockReason: e.target.value,
                        }))
                      }
                      rows={2}
                      placeholder="Neden bloke edildi?"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: "addresses",
        header: (
          <>
            <MapPin className="w-4 h-4 mr-2" /> Adresler
          </>
        ),
        content: (
          <div className="space-y-4 p-2">
            <div className="flex justify-between items-center">
              <h3 className="font-bold">Adres Listesi</h3>
              <IconTextButton
                onClick={addAddress}
                icon={Plus}
                className="btn btn-new"
              >
                Adres Ekle
              </IconTextButton>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-4 pr-2">
              {formData.addresses.map((addr, i) => (
                <div key={addr.id} className="p-4 card rounded-xl relative">
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-5">
                      <label className="text-[10px] font-bold uppercase">
                        Adres Başlığı
                      </label>
                      <InputText
                        className="w-full"
                        value={addr.title}
                        onChange={(e) =>
                          updateAddress(i, "title", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-span-4">
                      <label className="text-[10px] font-bold uppercase">
                        Tür
                      </label>
                      <Dropdown
                        className="w-full"
                        value={addr.addressType}
                        options={[
                          { label: "Fatura", value: "BILLING" },
                          { label: "Sevkiyat", value: "SHIPPING" },
                        ]}
                        onChange={(e) =>
                          updateAddress(i, "addressType", e.value)
                        }
                      />
                    </div>
                    <div className="col-span-3 flex items-center gap-2 pt-4">
                      <Checkbox
                        inputId={`def-${i}`}
                        checked={addr.isDefault}
                        onChange={(e) =>
                          updateAddress(i, "isDefault", e.checked)
                        }
                      />
                      <label htmlFor={`def-${i}`} className="text-xs">
                        Varsayılan
                      </label>
                    </div>
                    <div className="col-span-12">
                      <label className="text-[10px] font-bold uppercase text-slate-400">
                        Açık Adres
                      </label>
                      <InputTextarea
                        className="w-full"
                        rows={2}
                        value={addr.addressLine}
                        onChange={(e) =>
                          updateAddress(i, "addressLine", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => removeAddress(i)}
                    className="absolute -top-2 -right-2 bg-white border text-danger p-1 rounded-full shadow-sm hover:bg-bgAlt"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        ),
      },
      {
        id: "pool-discount",
        header: (
          <>
            <Wallet className="w-4 h-4 mr-2" /> Havuz & İskonto Ayarları
          </>
        ),
        content: (
          <div className="space-y-4 p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between col-span-2 md:col-span-1 p-3 bg-bgAlt rounded-lg border border-borderColor">
                <label className="font-bold text-sm text-primary flex items-center gap-2">
                  <Building2 size={16} /> Bayi Mi?
                </label>
                <InputSwitch
                  checked={formData.isDealer}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, isDealer: e.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between col-span-2 md:col-span-1 p-3 border rounded-lg">
                <label className="font-bold text-sm">Tahsilat Havuza Eklensin mi?</label>
                <InputSwitch
                  checked={formData.includeInPoolEarn}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, includeInPoolEarn: e.value }))
                  }
                />
              </div>

              <div className="flex items-center justify-between col-span-2 md:col-span-1 p-3 border rounded-lg">
                <label className="font-bold text-sm">Havuz İndirimi Kullanabilir mi?</label>
                <InputSwitch
                  checked={formData.canUsePoolDiscount}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, canUsePoolDiscount: e.value }))
                  }
                />
              </div>

              <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
                <label className="font-bold text-sm">Tahsilat Aktarım Tipi</label>
                <Dropdown
                  value={formData.collectionPoolType}
                  options={[
                    { label: "Plasiyerin Kendi Cüzdanına (PERSONAL)", value: "PERSONAL" },
                    { label: "Carinin Kendi Cüzdanına (GENERAL)", value: "GENERAL" },
                  ]}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, collectionPoolType: e.value }))
                  }
                />
              </div>
              
              <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
                <label className="font-bold text-sm">Özel Tahsilat Kazanım Oranı (%)</label>
                <InputNumber
                  value={formData.customEarnRate}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, customEarnRate: e.value ?? null }))
                  }
                  suffix=" %"
                  min={0}
                  max={100}
                  placeholder="Global kural baz alınır"
                />
              </div>

              <div className="flex flex-col gap-2 col-span-2 md:col-span-1">
                <label className="font-bold text-sm">Özel Maks. İndirim Oranı (%)</label>
                <InputNumber
                  value={formData.customMaxDiscountRate}
                  onValueChange={(e) =>
                    setFormData((p) => ({ ...p, customMaxDiscountRate: e.value ?? null }))
                  }
                  suffix=" %"
                  min={0}
                  max={100}
                  placeholder="Global kural baz alınır"
                />
              </div>
            </div>
          </div>
        ),
      },
    ],
    [formData, customer],
  );

  return (
    <Dialog
      header={customer ? "Müşteri Düzenle" : "Yeni Müşteri Ekle"}
      visible={isOpen}
      onHide={onClose}
      modal
      maximizable
      resizable={false}
      className="w-full lg:w-7/12"
      footer={
        <div className="flex justify-end gap-2">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">
            İptal
          </IconTextButton>
          <IconTextButton
            onClick={handleSubmit}
            icon={Save}
            className="btn-new"
          >
            Kaydet
          </IconTextButton>
        </div>
      }
    >      {employeeSelect.modal}
      <ETabs
        tabs={tabs}
        initialActiveIndex={activeIndex}
        onTabChange={setActiveIndex}
      />
    </Dialog>
  );
};
